import React, { useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API_ROOT } from "@/lib/api";
import { FileSpreadsheet, FileText, Boxes, History, Factory, Calendar, RotateCcw } from "lucide-react";

const REPORTS = [
  { key: "inventory", title: "Inventory", desc: "Full SKU list with qty, min stock, supplier, category", icon: Boxes, dateField: "created_at" },
  { key: "movements", title: "Stock movements", desc: "All goods in / out / production movements", icon: History, dateField: "timestamp" },
  { key: "production", title: "Production history", desc: "All production orders with status & lines", icon: Factory, dateField: "created_at" },
];

const PRESETS = [
  { label: "All time", days: null },
  { label: "Today", days: 0 },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "12 months", days: 365 },
];

function isoDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toISOString().slice(0, 10);
}

function isoDateTimeStartOfDay(d) {
  if (!d) return null;
  const dt = new Date(d + "T00:00:00.000Z");
  return dt.toISOString();
}

function isoDateTimeEndOfDay(d) {
  if (!d) return null;
  const dt = new Date(d + "T23:59:59.999Z");
  return dt.toISOString();
}

export default function Reports() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const applyPreset = (days) => {
    if (days === null) {
      setFrom(""); setTo(""); return;
    }
    const today = new Date();
    const t = new Date(today.getTime());
    const f = new Date(today.getTime());
    f.setDate(t.getDate() - days);
    setFrom(isoDate(f));
    setTo(isoDate(t));
  };

  const reset = () => { setFrom(""); setTo(""); };

  const download = async (key, kind) => {
    try {
      const token = localStorage.getItem("operon_token");
      const params = new URLSearchParams();
      const fIso = isoDateTimeStartOfDay(from);
      const tIso = isoDateTimeEndOfDay(to);
      if (fIso) params.set("from_date", fIso);
      if (tIso) params.set("to_date", tIso);
      const url = `${API_ROOT}/reports/${key}.${kind}${params.toString() ? "?" + params.toString() : ""}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("export failed");
      const blob = await resp.blob();
      // Try to read filename from Content-Disposition header
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `${key}_report.${kind}`;
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(objUrl);
      toast.success(`Exported ${key} ${kind.toUpperCase()}`);
    } catch (e) { toast.error("Export failed"); }
  };

  const rangeLabel = from || to ? `${from || "earliest"} → ${to || "now"}` : "All time";

  return (
    <AppShell title="Reports" subtitle="Export operational data as PDF or Excel — pick a date range or quick preset">
      {/* Date range picker */}
      <div className="rounded-2xl border border-border bg-card p-4 mb-4" data-testid="reports-daterange-block">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4 text-primary" />
          <div className="text-sm font-medium">Date range</div>
          <span className="ml-auto text-xs text-muted-foreground">Active: <span className="font-mono">{rangeLabel}</span></span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-[11px]">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="reports-from-date" className="w-[160px]" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="reports-to-date" className="w-[160px]" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <Button key={p.label} type="button" variant="outline" size="sm" onClick={() => applyPreset(p.days)} data-testid={`reports-preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}>{p.label}</Button>
            ))}
            <Button type="button" variant="ghost" size="sm" onClick={reset} className="text-muted-foreground" data-testid="reports-reset"><RotateCcw className="w-3.5 h-3.5 mr-1" />Reset</Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {REPORTS.map((r) => (
          <div key={r.key} className="rounded-2xl border border-border bg-card p-5 shadow-sm" data-testid={`report-card-${r.key}`}>
            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><r.icon className="w-4 h-4" /></div>
            <div className="mt-3 font-heading font-semibold">{r.title}</div>
            <div className="text-sm text-muted-foreground mt-1">{r.desc}</div>
            <div className="mt-4 flex items-center gap-2">
              <Button size="sm" onClick={() => download(r.key, "pdf")} data-testid={`reports-export-pdf-${r.key}`}><FileText className="w-4 h-4 mr-1.5" />PDF</Button>
              <Button size="sm" variant="secondary" onClick={() => download(r.key, "xlsx")} data-testid={`reports-export-xlsx-${r.key}`}><FileSpreadsheet className="w-4 h-4 mr-1.5" />Excel</Button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-card p-5">
        <h3 className="font-heading font-semibold">Tips</h3>
        <ul className="mt-2 text-sm text-muted-foreground list-disc pl-5 space-y-1">
          <li>Date range filters are applied to <code className="font-mono text-[11px] px-1 py-0.5 rounded bg-muted">created_at</code> for inventory & production, and <code className="font-mono text-[11px] px-1 py-0.5 rounded bg-muted">timestamp</code> for movements.</li>
          <li>PDFs are branded with your company logo (configure in Settings).</li>
          <li>Excel files include unit cost and total value calculations.</li>
          <li>Exports include your company's own data only — multi-tenant isolation is enforced.</li>
        </ul>
      </div>
    </AppShell>
  );
}
